/* eslint-disable */

import {
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import { androidEvents, androidParams } from "./android/arrays";
import {
  Box,
  Button,
  Container,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ICodeBlock } from "../main/components/codeblock";
import { IProgress, ISessionProps, OSType } from "../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { IOption } from "./SendInappEventParam";
import {
  Typography,
  ChipSelection,
  MultiSelectMenu,
} from "@appsflyer/fe-ui-core";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import {
  amplitudeTrack_continue,
  amplitudeTrack_inputChange,
  InputType,
} from "../services/utils";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import CloseIcon from "@mui/icons-material/Close";
import { EvgenEvent, EvgenEventsObject } from "./IEvgen";
import { iosEvents, iosParameter } from "./ios/arrays";
import ExplanationTooltip from "../main/components/ExplanationTooltip";
import transition from "../main/components/transition";
import { getCodeSnippet, interpolateTemplate } from "./codeTemplateUtils";
import {
  createAndroidSteps,
  createIOSteps,
  createFlutterSteps,
  createReactNativeSteps,
} from "./steps/platformSteps";
import { createDesignStep } from "./steps/designStep";
import { createLogEventStep } from "./steps/logEventStep";
import { useEvgenInapp } from "./hooks/useEvgenInapp";
import { useInappEventForm } from "./hooks/useInappEventForm";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const EventsTable = (props: EvgenEventsObject) => {
  const { t } = useTranslation();

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("ip_AppEvents_Evgen_ename")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_epurpose")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_etrigger")}</TableCell>
            <TableCell>{t("ip_AppEvents_Evgen_eparams")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props?.events.map((row, indx) => (
            <TableRow key={row.eventIdentifier + "_" + indx} hover>
              <TableCell>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  flexDirection={"column"}
                  gap={1}
                >
                  <div>
                    <Chip
                      label={row.eventType ?? ""}
                      size="medium"
                      variant="outlined"
                      color={
                        row.eventType == "predefined" ? "primary" : "success"
                      }
                    />
                  </div>
                  <code>{row.eventIdentifier}</code>
                </Box>
              </TableCell>
              <TableCell>{row.eventPurposeDescription}</TableCell>
              <TableCell>{row.eventTriggerDescription}</TableCell>
              <TableCell>
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  {row.parameters.map((param, idx) => {
                    return (
                      <span key={param.parameterIdentifier + "_" + idx}>
                        <code>{param.parameterIdentifier}</code>
                      </span>
                    );
                  })}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface MultiSelectControlProps {
  options: IOption[];
  onSelectedEventsChange?: (selectedValues: IOption[]) => void;
}

const MultiSelectControl = (props: MultiSelectControlProps) => {
  const [selectedEventsValues, setSelectedEventsValues] = useState<IOption[]>(
    []
  );
  const { t } = useTranslation();

  useEffect(() => {
    props.onSelectedEventsChange?.(selectedEventsValues);
  }, [selectedEventsValues]);

  const shouldRenderDeleteIcon = selectedEventsValues.length > 0;
  return (
    <ChipSelection
      label={t("ipAppEvents_Evgen_filter") + ": "}
      chipDefaultValue="All"
      controlType="multiSelect"
      value={selectedEventsValues}
      onChange={setSelectedEventsValues}
      deleteIcon={
        shouldRenderDeleteIcon ? <CloseIcon fontSize="small" /> : undefined
      }
      onDelete={
        shouldRenderDeleteIcon ? () => setSelectedEventsValues([]) : undefined
      }
      renderComponent={({ value, onChange, isOpen, setIsOpen, anchorRef }) => (
        <MultiSelectMenu
          options={props.options}
          values={value}
          {...{
            onChange,
            isOpen,
            setIsOpen,
            anchorRef,
          }}
        />
      )}
    />
  );
};

const SendInapp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const { t } = useTranslation();
  const isFlutter =
    location.state.os === OSType.FlutterAndroid ||
    location.state.os === OSType.FlutterIOS;
  const isAndroid = location.state.os === OSType.Android;
  const isIOS =
    location.state.os === OSType.iOS || location.state.os === OSType.FlutterIOS;
  const isReactNativeAndroid = location.state.os === OSType.ReactNativeAndroid;
  const isReactNativeIOS = location.state.os === OSType.ReactNativeIOS;
  const isReactNative = isReactNativeAndroid || isReactNativeIOS;

  const {
    eventName,
    eventParams,
    hoveredRow,
    setHoveredRow,
    paramValues,
    handleEventNameTypeChange,
    handleEventNameValueChange,
    handleAddParameter,
    handleRemoveParameter,
    handleParamTypeChange,
    handleParamValueChange,
    handleValueChange,
  } = useInappEventForm();
  const [responseListenerRequired, setResponseListenerRequired] =
    useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [generatedEventCodePrimary, setGeneratedEventCodePrimary] =
    useState<string>(
      isFlutter
        ? `const eventName = '<<Event name>>';
const eventValues = {
  << Event Param  >>: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>
};`
        : `Map<String, Object> eventValues = new HashMap<String, Object>();
eventValues.put(AFInAppEventParameterName.<< Event Param  >>, <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);
AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
    << Event Name  >>, eventValues);`
    );

  const [generatedEventCodeSecondary, setGeneratedEventCodeSecondary] =
    useState<string>(
      isFlutter
        ? ""
        : `val eventValues = HashMap<String, Any>()
eventValues[<< Event Param  >>] = <<PLACE_HOLDRER_FOR_PARAM_VALUE>>

AppsFlyerLib.getInstance().logEvent(
    applicationContext,
    << Event Name  >> , eventValues)`
    );

  const isPrefedinedParam = (param: string) => {
    if (
      isAndroid ||
      isReactNativeAndroid ||
      location.state.os === OSType.FlutterAndroid
    ) {
      return androidParams.find((prm) => prm.android_constant_name === param);
    }
    return iosParameter.find((prm) => prm.ios_constant_name === param);
  };

  const reactNativeStep2Code = !isReactNative
    ? `const eventName = '<< Event Name  >>';
      
const eventValues = {
  << Event Param  >>: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>
};`
    : (() => {
          const finalEventName =
            eventName.value?.trim() === ""
              ? `'<< Event Name  >>'`
              : eventName.type === "custom"
                ? `'${eventName.value}'`
                : `'${androidEvents.find((e) => e.android_constant_name === eventName.value)?.event_name || eventName.value}'`;

          const paramLines = eventParams
            .map((param, index) => {
              const eventParam = eventParams[index]?.value;
              const paramName =
                eventParam !== ""
                  ? androidParams.find(
                      (p) => p.android_constant_name === eventParam
                    )?.event_parameter_name || eventParam
                  : "<< Event Param  >>";
              return `  ${paramName}: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
            })
            .filter(Boolean);

          return `const eventName = ${finalEventName};
      
const eventValues = {
${paramLines.join(",\n")}
};`;
        })();

  const reactNativeStep3Code = `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  }
);`;

  useEffect(() => {
    // Step 3: Sending the event
    if (isFlutter) {
      const resolvedEventName = eventName.value?.trim()
        ? eventName.value
        : "<<Event name>>";
      const paramLinesDart = eventParams.map((param, index) => {
        const eventParam = param.value?.trim()
          ? param.value
          : "<< Event Param  >>";
        const line = `${eventParam}: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
        return index < eventParams.length - 1 ? `${line},` : line;
      });
      const updatedGeneratedEventCodeDart = `const eventName = '${resolvedEventName}';
const eventValues = {
  ${paramLinesDart.join("\n  ")}
};`;
      setGeneratedEventCodePrimary(updatedGeneratedEventCodeDart);
      setGeneratedEventCodeSecondary("");
      return;
    }

    if (isAndroid) {
      // Update generatedEventCode based on selectedRadioTwo
      const finalEventName =
        eventName.value?.trim() === ""
          ? eventName.type === "custom"
            ? '"<<Event name>>"'
            : "<<Event name>>"
          : eventName.type === "custom"
            ? `"${eventName.value}"`
            : eventName.value;

      // Use finalEventName like:
      const baseCodeJava = `AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
      ${finalEventName}, eventValues);`;

      const baseCodeKotlin = `AppsFlyerLib.getInstance().logEvent(
      applicationContext,
      ${finalEventName}, eventValues)`;

      const paramLinesJava = eventParams
        .map((param, index) => {
          const eventParam = eventParams[index]?.value;
          return `${
            eventParam !== ""
              ? `eventValues.put(${
                  isPrefedinedParam(eventParam)
                    ? `AFInAppEventParameterName.${eventParam}`
                    : `"${eventParam}"`
                },`
              : "eventValues.put(<< Event Param  >>,"
          } <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);`;
        })
        .filter(Boolean);

      const paramLinesKotlin = eventParams
        .map((param, index) => {
          const eventParam = eventParams[index]?.value;
          return `${
            eventParam !== ""
              ? `eventValues[${
                  isPrefedinedParam(eventParam)
                    ? `AFInAppEventParameterName.${eventParam}] = `
                    : `"${eventParam}" ] =`
                }`
              : "eventValues[<< Event Param  >>] ="
          } <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
        })
        .filter(Boolean);

      const updatedGeneratedEventCodeJava = `Map<String, Object> eventValues = new HashMap<String, Object>();
${paramLinesJava.join("\n")}
${baseCodeJava}`;

      const updatedGeneratedEventCodeKotlin = `val eventValues = HashMap<String, Any>()
${paramLinesKotlin.join("\n")}
${baseCodeKotlin}`;

      setGeneratedEventCodePrimary(updatedGeneratedEventCodeJava);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeKotlin);
    } else if (isReactNative) {
      // Keep the combined code for backward compatibility with other parts of the code
      const updatedGeneratedEventCodeReactNative = `${reactNativeStep2Code}

${reactNativeStep3Code}`;
      setGeneratedEventCodePrimary(updatedGeneratedEventCodeReactNative);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeReactNative);
    } else {
      // Update generatedEventCode based on selectedRadioTwo
      // Swift Step 2
      const baseCodeSwift = `AppsFlyerLib.shared().logEvent(name: ${
        (eventName && !(eventName.type === "custom")) ||
        (eventName && eventName.type === "custom")
          ? eventName.type === "custom"
            ? `"${eventName.value ?? "<<Event name>>"}"`
            : (eventName.value ?? "<<Event name>>")
          : "<<Event name>>"
      },`;

      // Objective-C Step 2
      const baseCodeObjC = `[[AppsFlyerLib shared] logEvent:${
        (eventName && !(eventName.type === "custom")) ||
        (eventName && eventName.type === "custom")
          ? eventName.type === "custom"
            ? `"${eventName.value ?? "<<Event name>>"}"`
            : (eventName.value ?? "<<Event name>>")
          : "<<Event name>>"
      }`;

      const paramLinesSwift = eventParams
        .map((param) => {
          const eventParam = param.value;
          return `${
            isPrefedinedParam(eventParam) ? eventParam : `"${eventParam}"`
          }: << Param value >>`;
        })
        .map((line, index, arr) =>
          index < arr.length - 1 ? `  ${line},` : `  ${line}`
        );

      const paramLinesObjC = eventParams
        .map((param) => {
          const eventParam = param.value;
          return `  ${
            isPrefedinedParam(eventParam) ? eventParam : `@"${eventParam}"`
          }: @<< Param value >>`;
        })
        .map((line, index, arr) =>
          index < arr.length - 1 ? line + "," : line
        );

      // Swift
      const updatedGeneratedEventCodeSwift = `${baseCodeSwift}
    values: [
    ${paramLinesSwift.join("\n")}
    ]
  );`;
      // Objective-C
      const updatedGeneratedEventCodeObjC = `${baseCodeObjC}
                                         withValues:@{
                                             ${paramLinesObjC.join("\n")}
                                         }];`;
      setGeneratedEventCodePrimary(updatedGeneratedEventCodeSwift);
      setGeneratedEventCodeSecondary(updatedGeneratedEventCodeObjC);
    }
    // setParamAdded(false);
  }, [
    eventName,
    eventName,
    eventName.type === "custom",
    eventParams,
    isAndroid,
    isFlutter,
  ]);

  useEffect(() => {
    const state: ISessionProps = location.state as ISessionProps;
    setProgress(state?.progress);
  }, [location.state]);

  const { os } = location.state || { os: "" };

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr[progressArr.length - 1].isListener = responseListenerRequired;

    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };

    if (tabValue == 1) {
      progressState.isEvgenTesting = false;
      progressState["eventName"] =
        eventName.type === "predefined"
          ? isFlutter
            ? eventName.value
            : ((isAndroid ||
                isReactNativeAndroid ||
                location.state.os === OSType.FlutterAndroid
              ? androidEvents.find(
                  (e) => e.android_constant_name === eventName.value
                )?.event_name
              : iosEvents.find((e) => e.ios_constant_name === eventName.value)
                  ?.event_name) ?? eventName.value)
          : eventName.value;
      progressState["eventParams"] = paramValues
        .filter((x) => x)
        .map((param) => {
          // look for the parameter within the param list and return the event parameter name
          // (unless it's custom and then the original value is returned)
          if (isFlutter) {
            return param;
          }

          if (
            isAndroid ||
            isReactNativeAndroid ||
            location.state.os === OSType.FlutterAndroid
          ) {
            return (
              androidParams.find((x) => x.android_constant_name === param)
                ?.event_parameter_name ?? param
            );
          } else {
            return (
              iosParameter.find((x) => x.ios_constant_name === param)
                ?.event_parameter_name ?? param
            );
          }
        });
      amplitudeTrack_continue(location.state, {
        responseListenerRequired,
        eventName: eventName.value,
        eventParams: paramValues,
      });
    } else if (tabValue == 0) {
      progressState.isEvgenTesting = true;
      progressState["evgenEventsObject"] = evgenEventsObject;

      amplitudeTrack_continue(location.state, {
        responseListenerRequired,
        isEvgenTesting: true,
        evgenEventsObject,
      });
    }

    sessionStorage.setItem("previousPage", location.pathname); // Save the current URL before navigation

    navigate(`/inapp/${os.toLowerCase()}/testtype`, {
      state: progressState,
    });
  };

  const codeProps: ICodeBlock = {
    codePrimary: getCodeSnippet("android.imports.basic"),
    codeSecondary: getCodeSnippet("android.kotlin.imports.basic"),
    showLineNumbers: true,
  };
  const codeProps2: ICodeBlock = {
    codePrimary: getCodeSnippet("android.imports.withConstants"),
    codeSecondary: getCodeSnippet("android.kotlin.imports.withConstants"),
    highlight: "2-3",
    highlightSecondary: "2-3",
    showLineNumbers: true,
  };

  const options: IOption[] = isFlutter
    ? (location.state.os === OSType.FlutterAndroid ? androidEvents : iosEvents).map(
        (event) => ({
          value: event["event_name"],
          label: event["event_name"],
        })
      )
    : isAndroid ||
      isReactNativeAndroid ||
      location.state.os === OSType.FlutterAndroid
      ? androidEvents.map((event) => ({
          value: event["android_constant_name"],
          label: event["event_name"],
        }))
      : iosEvents.map((event) => ({
          value: event["ios_constant_name"],
          label: event["event_name"],
        }));

  const getInitialCodeTemplate = (os: OSType) => {
    if (os === OSType.Android) {
      return {
        primary: getCodeSnippet("android.eventCode.java.template"),
        secondary: getCodeSnippet("android.eventCode.kotlin.template"),
      };
    } else if (
      os === OSType.ReactNativeAndroid ||
      os === OSType.ReactNativeIOS
    ) {
      const template = getCodeSnippet("reactNative.eventCode.template");
      return { primary: template, secondary: template };
    }
    return { primary: "", secondary: "" };
  };

  const [inappCodePrimary, setInappCodePrimary] = useState<string>(
    getInitialCodeTemplate(location.state.os).primary
  );
  const [inappCodeSecondary, setInappCodeSecondary] = useState<string>(
    getInitialCodeTemplate(location.state.os).secondary
  );
  const [inappCodeWListenerPrimary, setInappCodeWListenerPrimary] =
    useState<string>("");
  const [inappCodeWListenerSecondary, setInappCodeWListenerSecondary] =
    useState<string>("");

  const [evgenInappCodeJava, setEvgenInappCodePrimary] = useState<string>("");
  const [evgenInappCodeKotlin, setEvgenInappCodeSecondary] =
    useState<string>("");
  const [evgenInappCodeFlutter, setEvgenInappCodeFlutter] =
    useState<string>("");

  const [responseListRequired, setResponseListRequired] =
    useState<string>("No");

  const handleResponseChange = (event) => {
    setResponseListRequired(event.target.value);
    amplitudeTrack_inputChange(
      location.state,
      InputType.Radio,
      "responseListRequired",
      event.target.value
    );

    if (isFlutter) {
      return;
    }

    if (isAndroid) {
      updateEventCodeAndroid();
      updateEventCodeEvgenAndroid(event.target.value);
    } else if (isReactNative) {
      updateEventCodeReactNative();
      updateEventCodeEvgenReactNative(event.target.value);
    } else {
      updateCodeIOS();
      updateEventCodeEvgenIOS(event.target.value);
    }
  };

  const updateEventCodeAndroid = () => {
    const importsWithListener = getCodeSnippet("android.imports.withListener");
    const importsWithoutListener = getCodeSnippet(
      "android.imports.withConstants"
    );
    const successCallback = getCodeSnippet("android.withListener.java.success");
    const errorCallback = getCodeSnippet("android.withListener.java.error");
    const kotlinSuccessCallback = getCodeSnippet(
      "android.withListener.kotlin.success"
    );
    const kotlinErrorCallback = getCodeSnippet(
      "android.withListener.kotlin.error"
    );

    const code1Java = `${importsWithListener}

${inappCodePrimary.replace(
  `eventValues);`,
  `eventValues,
      new AppsFlyerRequestListener() {
${successCallback}
${errorCallback}
      }
  );`
)}`;
    const code2Java = `${importsWithoutListener}

${inappCodePrimary}`;

    const code1Kotlin = `${importsWithListener}

${inappCodeSecondary.replace(
  `eventValues)`,
  `eventValues,
    object : AppsFlyerRequestListener {
${kotlinSuccessCallback}
${kotlinErrorCallback}
    })`
)}`;
    const code2Kotlin = `${importsWithoutListener}

${inappCodeSecondary}`;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1Java);
      setInappCodeWListenerSecondary(code1Kotlin);
    } else {
      setInappCodeWListenerPrimary(code2Java);
      setInappCodeWListenerSecondary(code2Kotlin);
    }
  };

  const updateCodeIOS = () => {
    if (generatedEventCodePrimary === "") {
      const swiftTemplate = getCodeSnippet("ios.eventCode.swift.template");
      const objcTemplate = getCodeSnippet("ios.eventCode.objc.template");

      setInappCodeWListenerPrimary(
        interpolateTemplate(swiftTemplate, {
          EVENT_NAME: "<<Event name>>",
          PARAM_LINES: "<<Event Param>>: << Param value >>",
        })
      );
      setInappCodeWListenerSecondary(objcTemplate);
      return;
    }

    const swiftCompletionHandler = getCodeSnippet(
      "ios.withListener.swift.completionHandler"
    );
    const objcCompletionHandler = getCodeSnippet(
      "ios.withListener.objc.completionHandler"
    );

    const code1Swift = `${inappCodePrimary.replace(
      `]
  );`,
      ""
    )}],
${swiftCompletionHandler}`;
    const code2Swift = inappCodePrimary;

    const code1ObjC = `${inappCodeSecondary.replace(
      /withValues:@{|}];/g,
      (match) => {
        if (match === "withValues:@{") {
          return "eventValues:@{";
        }
        if (match === "}];") {
          return "";
        }
        return match; // Fallback for any unforeseen cases
      }
    )}} ${objcCompletionHandler}`;
    const code2ObjC = inappCodeSecondary;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1Swift);
      setInappCodeWListenerSecondary(code1ObjC);
    } else {
      setInappCodeWListenerPrimary(code2Swift);
      setInappCodeWListenerSecondary(code2ObjC);
    }
  };

  const updateEventCodeReactNative = () => {
    if (generatedEventCodePrimary === "") {
      const template = getCodeSnippet("reactNative.eventCode.template");
      const defaultCode = interpolateTemplate(template, {
        EVENT_NAME: "'<< Event Name  >>'",
        PARAM_LINES: "  << Event Param  >>: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>",
      });
      setInappCodeWListenerPrimary(defaultCode);
      setInappCodeWListenerSecondary(defaultCode);
      return;
    }

    const code1ReactNative = `${inappCodePrimary.replace(
      `eventValues,
  (err) => {
    console.error(err);
  }
);`,
      ""
    )},
  (err) => {
    console.error(err);
  }
);`;
    const code2ReactNative = inappCodePrimary;

    if (responseListRequired == "No") {
      setInappCodeWListenerPrimary(code1ReactNative);
      setInappCodeWListenerSecondary(code2ReactNative);
    } else {
      setInappCodeWListenerPrimary(code2ReactNative);
      setInappCodeWListenerSecondary(code2ReactNative);
    }
  };

  const updateEventCodeEvgenAndroid = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }
    console.log("evgenEventsObject", evgenEventsObject);
    console.log("evgenEventsObject.events", evgenEventsObject.events);

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeJava = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
Map<String, Object> eventValues = new HashMap<String, Object>();
${event.parameters.map((param) => {
  return `eventValues.put(${
    param.parameterType === "predefined"
      ? `AFInAppEventParameterName.${param.parameterIdentifier.toUpperCase()}`
      : `"${param.parameterIdentifier}"`
  }, <<PLACE_HOLDRER_FOR_PARAM_VALUE>>);`;
}).join(`
`)}
AppsFlyerLib.getInstance().logEvent(getApplicationContext(),
  ${
    event.eventType === "predefined"
      ? `AFInAppEventType.${event.eventIdentifier.toUpperCase()}`
      : `"${event.eventIdentifier}"`
  }, eventValues);`;
    }).join(`

`);

    const evgenInappCodeKotlin = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
val eventValues = HashMap<String, Any>()
${event.parameters.map((param) => {
  return `eventValues[${
    param.parameterType === "predefined"
      ? `AFInAppEventParameterName.${param.parameterIdentifier.toUpperCase()}`
      : `"${param.parameterIdentifier}"`
  }] = <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
}).join(`
`)}
 AppsFlyerLib.getInstance().logEvent(
    applicationContext,
${
  event.eventType === "predefined"
    ? `AFInAppEventType.${event.eventIdentifier.toUpperCase()}`
    : `"${event.eventIdentifier}"`
}, eventValues)`;
    }).join(`

`);

    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeJava);
      setEvgenInappCodeSecondary(evgenInappCodeKotlin);
    } else {
      const evgenInappCodeWListenerJava = evgenInappCodeJava
        .split(`eventValues);`)
        .join(
          `eventValues,
      new AppsFlyerRequestListener() {
        @Override
        public void onSuccess() {
          // YOUR CODE UPON SUCCESS
        }
        @Override
        public void onError(int i, String s) {
          // YOUR CODE FOR ERROR HANDLING
        }
      }
  );`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerJava);

      const evgenInappCodeWListenerKotlin = evgenInappCodeJava
        .split(`eventValues)`)
        .join(
          `eventValues,
    object : AppsFlyerRequestListener {
        override fun onSuccess() {
            Log.d(LOG_TAG, "Event sent successfully")
        }
        override fun onError(errorCode: Int, errorDesc: String) {
            Log.d(LOG_TAG, "Event failed to be sent:\n" +
                    "Error code: " + errorCode + "\n"
                    + "Error description: " + errorDesc)
        }
    })`
        );
      setEvgenInappCodeSecondary(evgenInappCodeWListenerKotlin);
    }
  };

  const updateEventCodeEvgenFlutter = (
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeDart = evgenEvents.map((event) => {
      const paramLinesDart = event.parameters.map((param, index) => {
        const line = `  ${param.parameterIdentifier}: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
        return index < event.parameters.length - 1 ? `${line},` : line;
      });
      
      return `// ${event.eventIdentifier} definition:
const eventName = '${event.eventIdentifier}';
const eventValues = {
${paramLinesDart.join("\n")}
};`;
    }).join(`

`);

    setEvgenInappCodeFlutter(evgenInappCodeDart);
  };

  const updateEventCodeEvgenIOS = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeSwift = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
AppsFlyerLib.shared().logEvent(name: ${
        iosEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.ios_constant_name ?? `"${event.eventIdentifier}"`
      },
    ${
      event.parameters.length
        ? `values: [
      ${event.parameters.map((param) => {
        return `${
          iosParameter.find(
            (prm) => prm.event_parameter_name == param.parameterIdentifier
          )?.ios_constant_name ?? `"${param.parameterIdentifier}"`
        }: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
      }).join(`,
      `)}
    ]`
        : `[
    ]`
    }
  );`;
    }).join(`

`);

    const evgenInappCodeObjC = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
[[AppsFlyerLib shared] logEventWithEventName:${
        iosEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.ios_constant_name ?? `"${event.eventIdentifier}"`
      }
${
  event.parameters.length
    ? `             withValues:@{
  ${event.parameters.map((param) => {
    return `                     ${
      iosParameter.find(
        (prm) => prm.event_parameter_name == param.parameterIdentifier
      )?.ios_constant_name ?? `"${param.parameterIdentifier}"`
    }: @<<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
  }).join(`
  `)}
                       }`
    : `{}
`
}];`;
    }).join(`

`);
    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeSwift);
      setEvgenInappCodeSecondary(evgenInappCodeObjC);
    } else {
      const evgenInappCodeWListenerSwift = evgenInappCodeSwift
        .split(
          `   ]
  );`
        )
        .join(
          `   ],
    completionHandler: { (response: [String : Any]?, error: Error?) in
      if let response = response {
        print("In app event callback Success: ", response)
      }
      if let error = error {
        print("In app event callback ERROR:", error)
      }
    }
);`
        );
      const evgenInappCodeWListenerObjC = evgenInappCodeObjC
        .split(`withValues:@{`)
        .join(`eventValues:@{`)
        .split(`}];`)
        .join(
          `  completionHandler:^(NSDictionary *response, NSError *error) {
                                   if (response) {
                                       NSLog(@"In app event callback Success: %@", response);
                                   }
                                   if (error) {
                                       NSLog(@"In app event callback ERROR: %@", error);
                                   }
                               }];`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerSwift);
      setEvgenInappCodeSecondary(evgenInappCodeWListenerObjC);
    }
  };

  const updateEventCodeEvgenReactNative = (
    isResponse: string,
    evgenEvents?: EvgenEvent[],
    filteredOptions?: IOption[]
  ) => {
    if (tabValue === 1) {
      return;
    }

    if (!evgenEvents || !evgenEvents.length) {
      evgenEvents = evgenEventsObject.events;
    }

    // apply filter
    if (filteredOptions) {
      if (filteredOptions.length) {
        const filters = filteredOptions.map((item) => item.value);
        evgenEvents = evgenEvents.filter((event) =>
          filters.includes(event.eventIdentifier)
        );
      }
    } else if (filteredEventOptions.length) {
      const filters = filteredEventOptions.map((item) => item.value);
      evgenEvents = evgenEvents.filter((event) =>
        filters.includes(event.eventIdentifier)
      );
    }

    const evgenInappCodeReactNative = evgenEvents.map((event) => {
      return `// ${event.eventIdentifier} definition:
const eventName = ${
        androidEvents.find((evt) => evt.event_name == event.eventIdentifier)
          ?.event_name ?? `'${event.eventIdentifier}'`
      };
const eventValues = {
  ${event.parameters.map((param) => {
    return `${
      androidParams.find(
        (prm) => prm.event_parameter_name == param.parameterIdentifier
      )?.event_parameter_name ?? param.parameterIdentifier
    }: <<PLACE_HOLDRER_FOR_PARAM_VALUE>>`;
  }).join(`,
  `)}
};

appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`;
    }).join(`

`);

    if (isResponse == "No") {
      setEvgenInappCodePrimary(evgenInappCodeReactNative);
      setEvgenInappCodeSecondary(evgenInappCodeReactNative);
    } else {
      // For React Native, promises already handle success/error, so use the same code
      const evgenInappCodeWListenerReactNative = evgenInappCodeReactNative
        .split(
          `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`
        )
        .join(
          `appsFlyer.logEvent(eventName, eventValues)
  .then((res) => {
    console.log(res);
  })
  .catch((err) => {
    console.error(err);
  });`
        );
      setEvgenInappCodePrimary(evgenInappCodeWListenerReactNative);
      setEvgenInappCodeSecondary(evgenInappCodeWListenerReactNative);
    }
  };

  useEffect(() => {
    if (generatedEventCodePrimary) {
      if (isFlutter) {
        return;
      }

      setInappCodePrimary(generatedEventCodePrimary);
      setInappCodeSecondary(generatedEventCodeSecondary);
      if (isAndroid) {
        setInappCodeWListenerPrimary(`import com.appsflyer.AppsFlyerLib;
import com.appsflyer.AFInAppEventType; // Predefined event names
import com.appsflyer.AFInAppEventParameterName; // Predefined parameter names

${generatedEventCodePrimary}`);
        setInappCodeWListenerSecondary(`import com.appsflyer.AppsFlyerLib
import com.appsflyer.AFInAppEventType // Predefined event names
import com.appsflyer.AFInAppEventParameterName // Predefined parameter names

  ${setGeneratedEventCodeSecondary}`);
      } else {
        setInappCodeWListenerPrimary(generatedEventCodePrimary);
        setInappCodeWListenerSecondary(generatedEventCodeSecondary);
      }
    }
  }, [generatedEventCodePrimary, generatedEventCodeSecondary, isAndroid, isFlutter]);

  const {
    evgenEventsObject,
    filteredEventOptions,
    filterEvgenEvents,
    handleFileUpload,
  } = useEvgenInapp({
    isFlutter,
    isAndroid,
    isReactNative,
    responseListRequired,
    updateEventCodeEvgenAndroid,
    updateEventCodeEvgenFlutter,
    updateEventCodeEvgenIOS,
    updateEventCodeEvgenReactNative,
  });

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const stepsAndroid = createAndroidSteps({
    t,
    lang: location.state.lang,
    codeProps,
    codeProps2,
  });

  const stepsIOS = createIOSteps({
    t,
    lang: location.state.lang,
  });

  const stepsFlutter = createFlutterSteps({ t });

  const stepsReactNative = createReactNativeSteps({ t });

  const flutterLogEventCode = `try {
  _appsflyerSdk?.logEvent(eventName, eventValues);
  debugPrint("In app event callback Success: $eventName");
} catch (error) {
  debugPrint("In app event callback ERROR: $error");
}`;

  const steps =
    isAndroid && !isFlutter
      ? stepsAndroid
      : isFlutter
        ? stepsFlutter
        : isReactNative
          ? stepsReactNative
          : stepsIOS;

  steps.push(
    createDesignStep({
      t,
      isFlutter,
      isReactNative,
      isAndroidFamily:
        isAndroid ||
        isReactNativeAndroid ||
        location.state.os === OSType.FlutterAndroid,
      tabValue,
      handleTabChange,
      a11yProps,
      CustomTabPanel,
      EventsTable,
      evgenEventsObject,
      handleFileUpload,
      eventName,
      eventParams,
      hoveredRow,
      setHoveredRow,
      handleEventNameTypeChange,
      handleEventNameValueChange,
      handleParamTypeChange,
      handleValueChange,
      handleParamValueChange,
      handleRemoveParameter,
      handleAddParameter,
      options,
      generatedEventCodePrimary,
      generatedEventCodeSecondary,
      reactNativeStep2Code,
    })
  );

  steps.push(
    createLogEventStep({
      t,
      isFlutter,
      isReactNative,
      tabValue,
      responseListRequired,
      responseListenerRequired,
      setResponseListenerRequired,
      handleResponseChange,
      inappCodeWListenerPrimary,
      inappCodeWListenerSecondary,
      evgenInappCodeJava,
      evgenInappCodeKotlin,
      evgenInappCodeFlutter,
      flutterLogEventCode,
      reactNativeStep3Code,
      evgenEventsObject,
      filterEvgenEvents,
      MultiSelectControl,
      ExplanationTooltip,
    })
  );

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography sx={{ mb: 2 }} variant="h1">
          {t("ip_SendInApp_android_sendInAppEventHeading")}
        </Typography>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component, shouldDisplayNavigationButtons }) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                {shouldDisplayNavigationButtons && (
                  <Box sx={{ mt: 2 }}>
                    {activeStep !== 0 && (
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleBack}
                      >
                        {t("general_Back")}
                      </Button>
                    )}
                    {activeStep !== steps.length - 1 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={
                          (isAndroid &&
                            !isFlutter &&
                            activeStep === 2 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject))) ||
                          (isIOS &&
                            !isFlutter &&
                            activeStep === 1 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject))) ||
                          (isReactNative &&
                            activeStep === 1 &&
                            ((tabValue == 1 &&
                              ((!(eventName.type === "custom") && !eventName) ||
                                (eventName.type === "custom" && !eventName) ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject))) ||
                          (isFlutter &&
                            activeStep === 1 &&
                            ((tabValue == 1 &&
                              (!eventName.value ||
                                eventParams.length === 0 ||
                                !eventParams[0]?.value)) ||
                              (tabValue == 0 && !evgenEventsObject)))
                        }
                      >
                        {activeStep === steps.length - 1
                          ? t("general_Finish")
                          : t("general_Next")}
                      </Button>
                    )}
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        {" "}
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          disabled={activeStep != steps.length - 1}
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(SendInapp);
